import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  DayButtonProps,
  DayPicker,
  getDefaultClassNames,
} from "react-day-picker";
import "react-day-picker/dist/style.css";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import { ScheduleV2 } from "../../../types";
import { CalendarEventCard } from ".";
import { getMySchedules } from "../../../redux/features/schedule/ScheduleApi";
import { DateTimeUtil } from "../../../utils";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const AppCalendar: React.FC = () => {
  const [selectedDay, setSelectedDay] = useState<Date | undefined>();
  const dispatch = useAppDispatch();
  const [schedules, setSchedules] = useState<ScheduleV2[]>([]);
  const { schedulesMap, isLoadingSchedules } = useSelector(
    (state: RootState) => state.schedule
  );
  const [currentMonth, setCurrentMonth] = useState(() => new Date());

  useEffect(() => {
    const today = new Date();
    const todayStr = format(today, "yyyy-MM-dd");
    const todaySchedules = schedulesMap[todayStr];

    if (todaySchedules && todaySchedules.length > 0) {
      setSelectedDay(today);
      setSchedules(todaySchedules);
    } else {
      setSelectedDay(undefined);
      setSchedules([]);
    }
  }, [schedulesMap]);

  useEffect(() => {
    const today = new Date();
    const lastMonthStartDate = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1
    );
    const nextMonthLastDate = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0,
      23,
      59,
      59
    );

    dispatch(
      getMySchedules({
        published_only: "true",
        start_date: DateTimeUtil.formatDateToMySQL(lastMonthStartDate, true),
        end_date: DateTimeUtil.formatDateToMySQL(nextMonthLastDate, true),
      })
    );
  }, [dispatch]);

  const handleMonthChange = (newMonth: Date) => {
    const startOfMonth = new Date(
      newMonth.getFullYear(),
      newMonth.getMonth(),
      1
    );
    const endOfMonth = new Date(
      newMonth.getFullYear(),
      newMonth.getMonth() + 1,
      0,
      23,
      59,
      59
    );

    dispatch(
      getMySchedules({
        published_only: "true",
        start_date: DateTimeUtil.formatDateToMySQL(startOfMonth, true),
        end_date: DateTimeUtil.formatDateToMySQL(endOfMonth, true),
      })
    );
  };

  const CalendarHeader = ({
    month,
    onMonthChange,
  }: {
    month: Date;
    onMonthChange: (date: Date) => void;
  }) => {
    const buttonSize = 28;
    const goToPrevMonth = () => {
      const prev = new Date(month.getFullYear(), month.getMonth() - 1, 1);
      onMonthChange(prev);
    };

    const goToNextMonth = () => {
      const next = new Date(month.getFullYear(), month.getMonth() + 1, 1);
      onMonthChange(next);
    };

    return (
      <div className="flex items-center justify-between px-4 py-2 bg-white">
        <button
          onClick={goToPrevMonth}
          className="text-blue-500 p-1 rounded-full hover:bg-blue-100"
        >
          <ChevronLeft size={buttonSize} />
        </button>
        <h2 className="text-lg font-semibold">{format(month, "MMMM yyyy")}</h2>
        <button
          onClick={goToNextMonth}
          className="text-blue-500 p-1 rounded-full hover:bg-blue-100"
        >
          <ChevronRight size={buttonSize} />
        </button>
      </div>
    );
  };

  const CustomDayButton = ({ day, modifiers, ...props }: DayButtonProps) => {
    const { date } = day;
    const dateStr = format(date, "yyyy-MM-dd");
    const hasSchedule = schedulesMap[dateStr]?.length > 0;

    const isToday =
      format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
    const isSelected = selectedDay?.toDateString() === date.toDateString();

    const handleClick = () => {
      const schedules = schedulesMap[dateStr];
      if (schedules?.length) {
        setSchedules(schedules);
        setSelectedDay(date);
      } else {
        setSchedules([]);
        setSelectedDay(date); // ← select it anyway even if empty
      }
    };

    return (
      <button
        {...props}
        type="button"
        onClick={handleClick}
        className="flex flex-col items-center justify-center w-full h-full p-1"
      >
        <div
          className={`
                        w-8 h-8 flex items-center justify-center rounded-full text-sm transition-all
                        ${isSelected ? "ring-2 ring-blue-500" : ""}
                        ${
                          isToday
                            ? "border border-blue-500 text-blue-500 font-semibold"
                            : ""
                        }
                    `}
        >
          {date.getDate()}
        </div>
        {hasSchedule && (
          <span className="w-2 h-2 mt-2 rounded-full bg-blue-500" />
        )}
      </button>
    );
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center mt-6">
      {/* Calendar Section */}
      <div className="bg-white px-2 ">
        <CalendarHeader month={currentMonth} onMonthChange={setCurrentMonth} />
        <DayPicker
          mode="single"
          selected={selectedDay}
          className="text-gray-800"
          weekStartsOn={1}
          month={currentMonth}
          components={{
            DayButton: CustomDayButton,
            // PreviousMonthButton: CalendarNavButton,
            // NextMonthButton: CalendarNavButton,
            MonthCaption: (props) => {
              return <></>;
            },
            // CaptionLabel: CustomCaptionLabel,
          }}
          captionLayout="label"
          hideNavigation={true}
          onMonthChange={handleMonthChange}
          classNames={{
            chevron: `${
              getDefaultClassNames().chevron
            } w-full h-fullm text-gray-600`,
          }}
          // styles={{
          //     head: { fontWeight: "600", fontSize: "0.875rem", color: "#6B7280" },
          //     week: { borderBottom: "1px solid #E5E7EB" },
          //     weekdays: {
          //         fontWeight: "600",
          //         fontSize: "0.875rem",
          //         color: "#6B7280",
          //         borderBottom: "1px solid #E5E7EB",
          //     },
          //     head_cell: {
          //         width: "60px",
          //     },
          //     table: {
          //         maxWidth: "none",
          //     },
          //     day: {
          //         padding: "0.75rem",
          //         // margin: "0.2rem",
          //         margin: "auto",
          //         fontSize: "0.875rem",
          //         textAlign: "center",
          //         borderRadius: "0.5rem",
          //         transition: "background-color 0.2s, color 0.2s",
          //     },
          //     selected: {
          //         backgroundColor: "rgb(34, 197, 94)",
          //         color: "white",
          //     },
          //     today: {
          //         fontWeight: "bold",
          //         color: "white",
          //         backgroundColor: "#3B82F6",
          //         borderRadius: "9999px",
          //         padding: "0.5rem",
          //         border: "2px solid #2563EB",
          //     },
          //     outside: { color: "#D1D5DB" },
          // }}
          styles={{
            head: { fontWeight: "600", fontSize: "0.8rem", color: "#6B7280" },
            week: { borderBottom: "1px solid #E5E7EB" },
            weekdays: {
              fontWeight: "600",
              fontSize: "0.8rem",
              color: "#6B7280",
              borderBottom: "1px solid #E5E7EB",
            },
            head_cell: {
              width: "auto",
              marginTop: "2rem",
              padding: "2.5rem 0",
              minWidth: "0",
              whiteSpace: "nowrap",
            },
            table: {
              width: "100%",
              maxWidth: "100%",
              //   tableLayout: "fixed", // evenly distributes columns, prevents overflow
            },
            day: {
              padding: "1rem 0",
              fontSize: "1rem",
              textAlign: "center",
              width: "calc(100vw / 7)",
              borderRadius: "0.5rem",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              transition: "background-color 0.2s, color 0.2s",
            },
            selected: {
              backgroundColor: "rgb(34, 197, 94)",
              color: "white",
            },
            today: {
              fontWeight: "bold",
              color: "white",
              backgroundColor: "#3B82F6",
              borderRadius: "9999px",
              padding: "0.4rem",
              border: "2px solid #2563EB",
            },
            outside: { color: "#D1D5DB" },
          }}
        />
      </div>

      {/* Events Section */}
      <div className="flex-grow-6 bg-white w-full items-center rounded-t-lg px-2 pt-2 overflow-y-auto">
        {isLoadingSchedules ? (
          <div className="flex justify-center items-start py-4">
            <Loader2 className="animate-spin text-blue-500 w-6 h-6" />
          </div>
        ) : schedules && schedules.length > 0 ? (
          schedules.map((schedule: ScheduleV2, index) => (
            <CalendarEventCard key={index} schedule={schedule} />
          ))
        ) : (
          <p className="text-center text-gray-500 mt-4 font-bold">No events available</p>
        )}
      </div>
    </div>
  );
};

export default AppCalendar;
